<script lang="ts" setup>
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import { i18n } from '@/i18n';
import { computed, ref, watch } from 'vue';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';

// props
const props = defineProps<{
  modalState: boolean;
  initialStartYearMonth?: IYearMonth;
  initialEndYearMonth?: IYearMonth;
}>();

const emits = defineEmits<{
  (e: 'close'): void;
  (e: 'apply', startYearMonth: IYearMonth, endYearMonth: IYearMonth): void;
}>();

// 変数
const t = i18n.t;
const locale = computed(() => i18n.locale);

// 開始年月
const startYearMonth = ref<IYearMonth>({ year: 0, month: 0 });

// 終了年月
const endYearMonth = ref<IYearMonth>({ year: 0, month: 0 });

/**
 * モーダルが開かれた時にデフォルト値を設定
 */
watch(
  () => props.modalState,
  (newValue) => {
    if (newValue) {
      // propsで初期値が渡されている場合はそれを使用
      if (props.initialStartYearMonth && props.initialStartYearMonth.year !== 0) {
        startYearMonth.value = { ...props.initialStartYearMonth };
      } else {
        // 現在の年月をデフォルトとして設定
        const currentDate = new Date();
        startYearMonth.value = DateService.convertDateToIYearMonth(currentDate);
      }

      if (props.initialEndYearMonth && props.initialEndYearMonth.year !== 0) {
        endYearMonth.value = { ...props.initialEndYearMonth };
      } else {
        // 終了月は開始月 + 3ヶ月をデフォルトとして設定
        endYearMonth.value = DateService.getShiftedIYearMonthByMonths(startYearMonth.value.year, startYearMonth.value.month, 3);
      }
    }
  },
  { immediate: true }
);

/**
 * ダウンロードボタンの非活性状態
 */
const isDisabledApplyButton = computed(() => {
  return (
    startYearMonth.value.year === 0 || endYearMonth.value.year === 0 || startYearMonth.value.month === 0 || endYearMonth.value.month === 0
  );
});

/**
 * 開始年月選択時の処理
 * @param yearMonth - 選択された年月
 */
const onSelectedStartYearMonth = (yearMonth: IYearMonth) => {
  startYearMonth.value = yearMonth;
};

/**
 * 終了年月選択時の処理
 * @param yearMonth - 選択された年月
 */
const onSelectedEndYearMonth = (yearMonth: IYearMonth) => {
  endYearMonth.value = yearMonth;
};

/**
 * ダウンロードボタンクリック時の処理
 */
const onApply = () => {
  emits('apply', startYearMonth.value, endYearMonth.value);
};
</script>

<template>
  <InfoModal
    :modal-state="modalState"
    :back-button-label="t('もどる')"
    :next-button-label="t('ダウンロード')"
    :modal-width="500"
    :is-disabled-next-button="isDisabledApplyButton"
    @on-back="emits('close')"
    @on-next="onApply"
  >
    <template #title>{{ t('客先内示ダウンロード') }}</template>

    <div class="download-customer-forecast-modal__main-content main-content">
      <div class="main-content__label">{{ t('期間の選択') }}</div>
      <div class="main-content__picker-container">
        <div class="main-content__picker-item">
          <label class="main-content__picker-label">{{ t('開始月') }}</label>
          <MonthPicker
            class="main-content__calendar download-customer-forecast-calendar"
            :model-value="startYearMonth"
            :locale="locale"
            :prop-format="'yyyy / MM'"
            :max-date="endYearMonth"
            @update:model-value="onSelectedStartYearMonth($event)"
          />
        </div>
        <div class="main-content__picker-item">
          <label class="main-content__picker-label">{{ t('終了月') }}</label>
          <MonthPicker
            class="main-content__calendar download-customer-forecast-calendar"
            :model-value="endYearMonth"
            :locale="locale"
            :prop-format="'yyyy / MM'"
            :min-date="startYearMonth"
            @update:model-value="onSelectedEndYearMonth($event)"
          />
        </div>
      </div>
    </div>
  </InfoModal>
</template>

<style lang="scss" scoped>
.main-content {
  background-color: $backgroundDefault;
  border-radius: 8px;
  display: grid;
  grid-template-rows: auto auto;
  gap: 16px;
  padding: 16px;

  &__label {
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    color: $textDefault;
  }

  &__picker-container {
    display: grid;
    grid-template-columns: auto auto;
    gap: 24px;
    align-items: center;
  }

  &__picker-item {
    display: grid;
    grid-template-columns: auto auto;
    gap: 8px;
    align-items: center;
  }

  &__picker-label {
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
  }

  &__calendar {
    min-width: 120px;
    max-width: 120px;
    height: 36px;
    background-color: $semanticWhite;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }
}
</style>

<style lang="scss">
.download-customer-forecast-calendar input {
  font-family: Noto Sans JP;
  font-size: 14px;
  font-weight: 400;
  color: $textDefault;
}
</style>
